import { Button, Checkbox, FileInput, Group, LoadingOverlay, Select, Stack, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import type { JSX } from "react";

import Hider from "../Hider";
import TagsInput from "../TagsInput";
import { DOCUMENT_TYPES, type DocumentType, getMime } from "../utils";

export interface IFormValues {
    documentType: DocumentType;
    title: string;
    author: string;
    publication: string;
    content: string;
    file: File | null;
    tags: string[];
    url: string;
    reprocess: boolean;
}

interface IProps {
    handleSubmit: (values: IFormValues) => void;
    initialValues?: IFormValues;
    edit: boolean;
    fetching: boolean;
    filePlaceholder?: string;
}

const DOCUMENT_FILE_TYPES = ["PDF", "WORD", "POWERPOINT", "MARKDOWN", "AUDIO"];

function DocumentForm({ handleSubmit, initialValues, edit, fetching, filePlaceholder }: IProps): JSX.Element {
    const form = useForm<IFormValues>({
        initialValues: initialValues ?? {
            documentType: "PLAINTEXT",
            title: "",
            author: "",
            publication: "",
            content: "",
            url: "",
            file: null,
            tags: [],
            reprocess: true,
        },
        validate: {
            documentType: (value) => (value ? null : "Required"),
            title: (value) => (value ? null : "Required"),
            author: (value) => (value ? null : "Required"),
            content: (value, values) => {
                if (values.documentType === "PLAINTEXT" && !value) {
                    return "Required";
                }
                return null;
            },
            url: (value, values) => {
                if (values.documentType === "WEBPAGE" && !value) {
                    return "Required";
                }
                return null;
            },
            file: (value, values) => {
                if (!DOCUMENT_FILE_TYPES.includes(values.documentType)) {
                    return null;
                }
                if (edit) {
                    return null;
                }
                if (!value) {
                    return "Required";
                }
                if (values.documentType === "AUDIO") {
                    if (value.size / 1024 / 1024 > 2000) {
                        return "File size too big";
                    }
                    if (!getMime("AUDIO").includes(value.type)) {
                        return "Unsupported audio format";
                    }
                }
                return null;
            },
        },
    });

    const showFileInput = DOCUMENT_FILE_TYPES.includes(form.values.documentType);

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack maw={800} mx="auto">
                <LoadingOverlay visible={fetching} />
                <Hider show={!edit}>
                    <Select
                        withAsterisk
                        label="Document type"
                        data={DOCUMENT_TYPES}
                        {...form.getInputProps("documentType")}
                    />
                </Hider>
                <TextInput withAsterisk label="Title" {...form.getInputProps("title")} />
                <TextInput withAsterisk label="Author" {...form.getInputProps("author")} />
                <TextInput label="Publication" {...form.getInputProps("publication")} />
                <TagsInput form={form} />
                <Hider show={edit}>
                    <Checkbox
                        mt="sm"
                        label="Re-process file"
                        {...form.getInputProps("reprocess", { type: "checkbox" })}
                    />
                </Hider>
                <Hider show={!edit || form.values.reprocess}>
                    <Hider show={form.values.documentType === "PLAINTEXT"}>
                        <Textarea
                            withAsterisk
                            autosize
                            minRows={3}
                            label="Content"
                            {...form.getInputProps("content")}
                        />
                    </Hider>
                    <Hider show={form.values.documentType === "WEBPAGE"}>
                        <TextInput withAsterisk label="URL" {...form.getInputProps("url")} />
                    </Hider>
                    <Hider show={showFileInput}>
                        <FileInput
                            label="Select file"
                            withAsterisk
                            fileInputProps={{ accept: getMime(form.values.documentType).join(",") }}
                            placeholder={filePlaceholder}
                            {...form.getInputProps("file")}
                        />
                    </Hider>
                </Hider>
                <Group justify="flex-end" mt="md">
                    <Button type="submit" disabled={fetching}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

export default DocumentForm;
